import React, { useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  Typography,
  Tooltip,
  Popover,
  makeStyles,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Button,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  FormControlLabel,
  Chip,
  Icon,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { updateTenantUser } from '@pingum/app-state-management/tenant/actions';

import {
  getRoleSimpleList,
  formatStringOfRolesIntoArray,
} from '../../../../../../utils/rolesHelper';
import styles from './styles';

const useStyles = makeStyles(styles);
const profileUserTenants = 'https://pingum.io/tenants';

export const UserRolesComponent = (props) => {
  const {
    tenant,
    tenantUser,
    isLoading,
    onlyOneOwner,
    infoButton,
  } = props;
  const dispatch = useDispatch();
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [editRoles, setEditRoles] = useState(formatStringOfRolesIntoArray(tenantUser.roles));
  const [editPassive, setEditPassive] = useState(tenantUser.passive);
  const { user: profile } = useSelector((state) => state.user);
  const userRoles = profile[profileUserTenants][tenant].roles;

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    // revert data
    setEditRoles([...formatStringOfRolesIntoArray(tenantUser.roles)]);
    setEditPassive(tenantUser.passive);
    setRemoveError(null);
  };

  const isCheckboxDisabled = (role) => {
    // user role is always disabled, must be removed from org to remove user permissions
    if (role === 'user') {
      return true;
    }
    // owner role can only be changed by another owner
    // TODO what if all owner roles are removed?
    if (role === 'owner' && userRoles.indexOf('owner') === -1) {
      return true;
    }
    return false;
  };

  const handleSaveRoles = () => {
    // must always be one owner
    if (onlyOneOwner && tenantUser.roles.indexOf('owner') >= 0 && editRoles.indexOf('owner') === -1) {
      setRemoveError('There must always be one owner in your organization');
      return;
    }
    setRemoveError(null);

    dispatch(updateTenantUser(tenant, tenantUser.auth0UserId, {
      email: tenantUser.email,
      firstName: tenantUser.firstName,
      lastName: tenantUser.lastName,
      roles: editRoles,
      passive: editPassive,
    }));
    handleClosePopover();
  };

  const handleRoleClick = (e) => {
    if (getRoleSimpleList().indexOf(e.target.value) === -1) {
      return;
    }
    let roleList = [...editRoles];
    if (roleList.indexOf(e.target.value) >= 0) {
      roleList = roleList.filter((role) => role !== e.target.value);
    } else {
      roleList.push(e.target.value);
    }
    setEditRoles(roleList);
  };

  const handlePassiveClick = () => {
    if (!editPassive) {
      setEditRoles([...editRoles.filter((role) => role === 'user')]);
    }
    setEditPassive(!editPassive);
  };

  return (
    <div>
      <Chip
        icon={<Icon className={classes.chipIcon}>face</Icon>}
        size="small"
        label={tenantUser.passive
          ? 'Passive'
          : 'Active'}
        className={classes.userChip}
        color={tenantUser.passive
          ? 'default'
          : 'secondary'}
      />
      <Tooltip title="Change roles" placement="left">
        <Typography
          variant="body2"
          onClick={(isLoading)
            ? () => {}
            : handleOpenPopover}
          className={classes.rolesTypography}
        >
          <IconButton aria-label="Delete" className={classes.editButton} size="small" disabled={isLoading}>
            <EditIcon fontSize="inherit" />
          </IconButton>
          {formatStringOfRolesIntoArray(tenantUser.roles).filter((role) => role !== 'restricted').join(', ')}
        </Typography>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List
          dense
          className={classes.listRoot}
          subheader={(
            <ListSubheader component="div" className={removeError && classes.error}>
              {removeError || tenantUser.email}

              {infoButton}
              {
                editRoles.indexOf('owner') === -1
                && (
                  <div className={classes.right}>
                    <FormControlLabel
                      labelPlacement="start"
                      className={classes.passiveActiveSwitch}
                      control={(
                        <Checkbox
                          checked={!editPassive}
                          onChange={handlePassiveClick}
                        />
                      )}
                      label={`${editPassive
                        ? 'Passive'
                        : 'Active'} User`}
                    />
                  </div>
                )
              }
            </ListSubheader>
          )}
        >
          <Divider />
          {getRoleSimpleList().map((role) => (
            <ListItem key={role}>
              <ListItemText primary={role} />
              <ListItemSecondaryAction>
                <Checkbox
                  edge="end"
                  value={role}
                  disabled={editPassive || isCheckboxDisabled(role)}
                  checked={editRoles.indexOf(role) !== -1}
                  onClick={handleRoleClick}
                />
              </ListItemSecondaryAction>
            </ListItem>

          ))}
        </List>
        <Divider />
        <div className={classes.footer}>
          <Button
            onClick={handleClosePopover}
            id="cancel-edit-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoles}
            color="primary"
            variant="contained"
            autoFocus
            id="confirm-edit-button"
          >
            Save
          </Button>
        </div>
      </Popover>
    </div>
  );
};

UserRolesComponent.propTypes = {
  tenant: PropTypes.string.isRequired,
  tenantUser: PropTypes.object.isRequired,
  onlyOneOwner: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  infoButton: PropTypes.any,
};
UserRolesComponent.defaultProps = {
  isLoading: false,
  infoButton: {},
};

export default UserRolesComponent;
